import * as Yup from 'yup';

const MAX_IMAGE_SIZE = 2 * 1024 * 1024; // 2 MB
const MAX_CARATULA_SIZE = 1 * 1024 * 1024; // 1 MB
const MAX_IMAGE_WIDTH = 1200; // 1200px
const MAX_IMAGE_HEIGHT = 1200; // 1200px
const MAX_CARATULA_WIDTH = 640; // 640px
const MAX_CARATULA_HEIGHT = 410; // 410px

const validateImage = (file: File, maxSize: number, maxWidth: number, maxHeight: number) => {
  return new Promise<boolean>((resolve) => {
    // Verificar tamaño del archivo
    if (file.size > maxSize) {
      resolve(false); // Tamaño excedido
      return;
    }

    const img = new Image();
    const reader = new FileReader();

    reader.onload = () => {
      img.onload = () => {
        // Verificar resolución de la imagen
        if (img.width > maxWidth || img.height > maxHeight) {
          resolve(false); // Resolución excedida
        } else {
          resolve(true); // Imagen válida
        }
      };
      img.src = reader.result as string;
    };

    reader.readAsDataURL(file);
  });
};


export const AnuncioValidationSchema = Yup.object({
  bdTipo: Yup.string().required('Tipo de anuncio es obligatorio'),
  idArea: Yup.number().required('Área es obligatoria').min(1, 'Seleccione un área válida'),
  categorias: Yup.array()
    .min(1, 'Debes seleccionar al menos una categoría')
    .max(1, 'No puedes seleccionar más de 1 categoría')
    .required('Categorías son obligatorias'),
  txTitulo: Yup.string().required('El título es obligatorio').min(3, 'El título debe tener al menos 3 caracteres'),
  txLugar: Yup.string()
    .nullable()
    .when('bdTipo', {
      is: 'E',
      then: (schema) => schema.required('El lugar es obligatorio'),
      otherwise: (schema) => schema.notRequired(),
    }),
  idTipoEvento: Yup.number()
    .nullable()
    .when('bdTipo', {
      is: 'E',
      then: (schema) => schema.min(1, "El tipo de evento es obligatorio"),
      otherwise: (schema) => schema.notRequired(),
    }),
  txtUrlMaps: Yup.string().when(['bdTipo', 'esEnEspol'], {
    is: (bdTipo: string, esEnEspol: boolean) => bdTipo === 'E' && !esEnEspol,
    then: (schema) =>
      Yup.string()
        .url('Debe ser una URL válida'),
    otherwise: (schema) => schema.nullable().notRequired()
  }),
  idUbicacion: Yup.number().when(['bdTipo', 'esEnEspol'], {
    is: (bdTipo: string, esEnEspol: boolean) =>
      bdTipo === 'E' && esEnEspol,
    then: (schema) =>
      schema
        .required('Debe seleccionar una ubicación válida')
        .moreThan(0, 'Debe seleccionar una ubicación válida'),
    otherwise: (schema) =>
      schema.nullable().notRequired()
  }),
  dtInicio: Yup.date()
    .nullable()
    .when('bdTipo', {
      is: 'E',
      then: (schema) =>
        schema.required('La fecha de inicio es obligatoria').test(
          'fecha-inicio-valida',
          'La fecha de inicio no es válida',
          (value) => value && new Date(value) >= new Date('1970-01-01')
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  dtFin: Yup.date()
    .nullable()
    .when('bdTipo', {
      is: 'E',
      then: (schema) =>
        schema
          .required('La fecha de fin es obligatoria')
          .test(
            'fecha-fin-valida',
            'La fecha de fin debe ser posterior a la fecha de inicio',
            function (value) {
              const { dtInicio } = this.parent; // Accede al valor de dtInicio
              if (dtInicio && value) {
                return new Date(value) > new Date(dtInicio);
              }
              return true; // Si no hay `dtInicio`, no aplica esta validación
            }
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
  // txUrl: Yup.string()
  //   .url('Por favor ingresa una URL válida') // Validar que sea una URL
  //   .notRequired(), // Campo no obligatorio
  dtPublicacion: Yup.date().required('La fecha de publicación es obligatoria'),
  bdPublicarAutomatico: Yup.boolean(),
  dtVigencia: Yup.date()
    .required('La fecha de vigencia es obligatoria')
    .test('fechaVigencia', 'La fecha de vigencia debe ser mayor o igual a la fecha de publicación', function (value) {
      const { dtPublicacion } = this.parent; // Accedemos a dtPublicacion usando this.parent
      if (value && dtPublicacion) {
        return new Date(value) >= new Date(dtPublicacion);
      }
      return true; // Si no hay fecha de vigencia o publicación, no se realiza la validación
    }),
  audiencias: Yup.array()
    .min(1, 'Debe seleccionar al menos una audiencia')
    .required('Este campo es obligatorio'),
  bdAmbito: Yup.string()
    .oneOf(["I", "A"], "Selecciona un ámbito válido") // Asegura que sea "I" o "A"
    .required("El ámbito es obligatorio"), // Marca como obligatorio
  txTexto: Yup.string()
    .required("La descripción es obligatoria") // Campo obligatorio
    .min(10, "La descripción debe tener al menos 10 caracteres") // Longitud mínima
    .max(500, "La descripción no puede exceder los 500 caracteres"),
  // // Validación para la imagen (2MB y 1200x1200px)
  Imagen: Yup.mixed<File>()
    .nullable()
    .test('fileFormat', 'Formato no soportado', function (value) {
      const { bdEstado, txImagen } = this.parent;
      if (bdEstado === 'B') {
        return true;
      }
      if (txImagen) {
        return true;
      }
      if (!value) {
        return this.createError({
          message: 'La imagen es obligatoria',
        });
      }
      return true; // Formato validado en otra prueba
    })
    .test('fileSizeAndResolution', 'La imagen debe ser de máximo 1MB y 1200x1200px', async function (value) {
      if (value) {
        const isValid = await validateImage(value, MAX_CARATULA_SIZE, MAX_IMAGE_WIDTH, MAX_IMAGE_HEIGHT);
        if (!isValid) {
          return this.createError({
            message: 'La imagen debe ser de máximo 1MB y 1200x1200px',
          });
        }
      }
      return true;
    }),

  ImagenCaratula: Yup.mixed<File>()
    .nullable()
    .test('fileFormat', 'Formato no soportado', function (value) {
      const { bdTipo, txtImagenCaratula } = this.parent;

      if (bdTipo !== 'E') {
        return true;
      }

      if (txtImagenCaratula) {
        return true;
      }

      if (!value) {
        return this.createError({
          message: 'La carátula es obligatoria',
        });
      }

      return true; // Formato validado en otra prueba
    })
    .test('fileSizeAndResolution', 'La carátula debe ser de máximo 1MB y 640x410px', async function (value) {
      if (value) {
        const isValid = await validateImage(value, MAX_CARATULA_SIZE, MAX_CARATULA_WIDTH, MAX_CARATULA_HEIGHT);
        if (!isValid) {
          return this.createError({
            message: 'La carátula debe ser de máximo 1MB y 640x410px',
          });
        }
      }
      return true;
    }),

});